import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { validatePassword } from '../lib/password-rules';
import { AuthCard } from '../components/AuthCard';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import { Alert } from '../components/Alert';
import { PasswordStrength } from '../components/PasswordStrength';

export function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSession, setHasSession] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function checkSession() {
      const { data } = await supabase.auth.getSession();
      if (!cancelled) {
        setHasSession(!!data.session);
      }
    }

    void checkSession();

    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!password) {
      setError('Password is required');
      return;
    }
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password,
      });

      if (updateError) {
        setError(updateError.message);
        setLoading(false);
        return;
      }

      setSuccess(true);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setLoading(false);
    }
  }

  if (hasSession === null) {
    return (
      <AuthCard title="Set new password">
        <div className="flex justify-center py-8">
          <svg
            className="animate-spin h-6 w-6 text-ll-cyan"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      </AuthCard>
    );
  }

  if (!hasSession) {
    return (
      <AuthCard title="Set new password">
        <Alert
          variant="error"
          message="Invalid or expired reset link. Please request a new password reset."
        />
        <p className="text-center text-sm text-ll-muted mt-4">
          <Link
            to="/forgot-password"
            className="text-ll-cyan hover:brightness-110 transition-colors"
          >
            Request new reset link
          </Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Set new password">
      {error && <Alert variant="error" message={error} onDismiss={() => setError(null)} />}

      {success ? (
        <>
          <Alert variant="success" message="Password updated successfully." />
          <p className="text-center text-sm text-ll-muted mt-4">
            <Link to="/sign-in" className="text-ll-cyan hover:brightness-110 transition-colors">
              Sign in with your new password
            </Link>
          </p>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          <Input
            label="New Password"
            type="password"
            placeholder="Enter new password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
            disabled={loading}
          />
          <PasswordStrength password={password} />
          <Input
            label="Confirm Password"
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            required
            disabled={loading}
          />

          <Button type="submit" loading={loading}>
            Update password
          </Button>
        </form>
      )}
    </AuthCard>
  );
}
